import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';

// project imports
import AppBarStyled from './AppBarStyled';
import HeaderContent from './HeaderContent';
import IconButton from 'components/@extended/IconButton';
import { getBreadcrumbLinks } from 'utils/breadcrumbLinks';

import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { MenuOrientation, DRAWER_WIDTH, MINI_DRAWER_WIDTH } from 'config';

// assets
import MenuFoldOutlined from '@ant-design/icons/MenuFoldOutlined';
import MenuUnfoldOutlined from '@ant-design/icons/MenuUnfoldOutlined';

// ==============================|| MAIN LAYOUT - HEADER ||============================== //

export default function Header() {
  const { pathname } = useLocation();
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { state } = useConfig();
  const { menuOrientation } = state;

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;
  const breadcrumbLinks = getBreadcrumbLinks(pathname);

  // header content
  const headerContent = useMemo(() => <HeaderContent />, []);

  // common header
  const mainHeader = (
    <Toolbar>
      {!isHorizontal && !drawerOpen ? (
        <IconButton
          aria-label="open drawer"
          onClick={() => handlerDrawerOpen(!drawerOpen)}
          edge="start"
          color="secondary"
          variant="light"
          sx={(theme) => ({
            color: 'text.primary',
            bgcolor: 'grey.100',
            ...theme.applyStyles('dark', { bgcolor: 'background.default' }),
            ml: { xs: 0, lg: -2 }
          })}
        >
          <MenuUnfoldOutlined />
        </IconButton>
      ) : null}
      <MuiBreadcrumbs
        aria-label="breadcrumb"
        maxItems={8}
        sx={{
          ml: 3,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' }
        }}
        separator="/"
      >
        {breadcrumbLinks.map((link, index) =>
          link.to ? (
            <Typography
              key={index}
              component={Link}
              to={link.to}
              variant="h6"
              color="text.secondary"
              sx={{ textDecoration: 'none' }}
            >
              {link.title}
            </Typography>
          ) : (
            <Typography key={index} variant="subtitle1" color="text.primary" sx={{ textDecoration: 'none' }}>
              {link.title}
            </Typography>
          )
        )}
      </MuiBreadcrumbs>
      {headerContent}
    </Toolbar>
  );

  // app-bar params
  const appBar = {
    position: 'fixed',
    color: 'inherit',
    elevation: 0,
    sx: {
      borderBottom: '1px solid',
      borderBottomColor: 'divider',
      zIndex: 1200,
      width: isHorizontal
        ? '100%'
        : { xs: '100%', lg: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : `calc(100% - ${MINI_DRAWER_WIDTH}px)` }
    }
  };

  return (
    <>
      {!downLG ? (
        <AppBarStyled open={drawerOpen} {...appBar}>
          {mainHeader}
        </AppBarStyled>
      ) : (
        <AppBar {...appBar}>{mainHeader}</AppBar>
      )}
    </>
  );
}
